import apiClient from "@/lib/api-client"
import type {
  Video,
  UploadSessionResponse,
  VideoOTPResponse,
  PaginatedResponse,
} from "@/types/api"

const buildQueryString = (params?: Record<string, any>): string => {
  if (!params) return ""
  const query = new URLSearchParams(params as any).toString()
  return query ? `?${query}` : ""
}

export const videoService = {
  // List videos
  getVideos: async (params?: {
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<Video>> => {
    return apiClient.get<PaginatedResponse<Video>>(
      `/vdocipher/videos/${buildQueryString(params)}`
    )
  },

  // Get video by ID
  getVideo: async (id: number): Promise<Video> => {
    return apiClient.get<Video>(`/vdocipher/videos/${id}/`)
  },

  // Direct upload (single request)
  uploadVideo: async (data: FormData): Promise<Video> => {
    return apiClient.post<Video>("/vdocipher/upload/", data)
  },

  // Create upload session (multi-step - step 1)
  createUploadSession: async (data: {
    title: string
    description?: string
  }): Promise<UploadSessionResponse> => {
    return apiClient.post<UploadSessionResponse>(
      "/vdocipher/upload-session/",
      data
    )
  },

  // Confirm upload (multi-step - step 3)
  confirmUpload: async (data: {
    video_id: number
    vdocipher_video_id: string
  }): Promise<Video> => {
    return apiClient.post<Video>("/vdocipher/confirm-upload/", data)
  },

  // Get video playback OTP
  getVideoOTP: async (id: number): Promise<VideoOTPResponse> => {
    return apiClient.post<VideoOTPResponse>(
      `/vdocipher/videos/${id}/otp/`
    )
  },

  // Check video status
  getVideoStatus: async (
    id: number,
    refresh?: boolean
  ): Promise<{
    id: number
    status: string
    duration: number
    is_ready: boolean
  }> => {
    return apiClient.get(
      `/vdocipher/videos/${id}/status/${buildQueryString(
        refresh ? { refresh: "true" } : undefined
      )}`
    )
  },

  // Update video metadata
  updateVideoMetadata: async (
    id: number,
    data: { title?: string; description?: string }
  ): Promise<Video> => {
    return apiClient.patch<Video>(`/vdocipher/videos/${id}/metadata/`, data)
  },

  // Update video (including custom thumbnail)
  updateVideo: async (id: number, data: FormData): Promise<Video> => {
    return apiClient.patch<Video>(`/vdocipher/videos/${id}/`, data)
  },

  // Delete video
  deleteVideo: async (id: number): Promise<void> => {
    await apiClient.delete(`/vdocipher/videos/${id}/delete/`)
  },
}
